import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, Download, Loader2, AlertTriangle, Wrench } from "lucide-react";
import { publicApi, publicFileUrl, formatPublicErr } from "../../lib/publicApi";

const fmtCAD = (v) => new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(Number(v || 0));
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-CA", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

const STATUS_STYLE = {
  Draft: "bg-slate-100 text-slate-700",
  Sent: "bg-sky-100 text-sky-800",
  Viewed: "bg-indigo-100 text-indigo-800",
  "Partially Paid": "bg-amber-100 text-amber-900",
  Paid: "bg-emerald-100 text-emerald-800",
  Overdue: "bg-rose-100 text-rose-800",
};

export default function PublicInvoiceView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicApi.get(`/invoice/${token}`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(formatPublicErr(e)))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 size={28} className="animate-spin text-slate-400"/>
      </div>
    );
  }
  if (err || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-6 text-center">
        <AlertTriangle size={36} className="text-rose-500 mb-3"/>
        <h1 className="font-bold text-xl text-slate-900 mb-2">Invoice unavailable</h1>
        <p className="text-sm text-slate-600 max-w-md">{err || "This invoice link is invalid or has been revoked. Please contact the company for a new link."}</p>
      </div>
    );
  }

  const inv = data.invoice;
  const customer = data.customer;
  const settings = data.settings;
  const balance = Math.max((inv.total || 0) - (inv.amount_paid || 0), 0);
  const primary = settings.primary_color || "#0F4C81";

  return (
    <div data-testid="public-invoice-view" className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {settings.has_logo ? (
              <img src={publicFileUrl(`/invoice/${token}/logo`)} alt="Logo" className="h-10 w-auto max-w-[120px] object-contain"/>
            ) : (
              <div className="w-10 h-10 rounded flex items-center justify-center shrink-0" style={{ background: primary }}>
                <Wrench className="text-white" size={20}/>
              </div>
            )}
            <div className="min-w-0">
              <div className="font-display font-bold text-slate-900 text-sm sm:text-base truncate">{settings.business_name || "Your provider"}</div>
              {settings.address && <div className="text-[11px] sm:text-xs text-slate-500 truncate">{settings.address}</div>}
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={() => window.print()} data-testid="public-invoice-print" className="px-3 py-2 text-xs sm:text-sm border border-slate-300 rounded hover:bg-slate-50 flex items-center gap-1.5">
              <Printer size={14}/> <span className="hidden sm:inline">Print</span>
            </button>
            <a href={publicFileUrl(`/invoice/${token}/pdf`)} target="_blank" rel="noopener noreferrer" data-testid="public-invoice-pdf" className="px-3 py-2 text-xs sm:text-sm rounded text-white flex items-center gap-1.5" style={{ background: primary }}>
              <Download size={14}/> <span className="hidden sm:inline">PDF</span>
            </a>
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10 print:py-2">
        <div className="bg-white border border-slate-200 rounded-md sm:rounded-lg shadow-sm overflow-hidden">
          <div className="px-5 sm:px-8 py-6 sm:py-8 border-b border-slate-100">
            <div className="flex justify-between items-start gap-4 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">Invoice</div>
                <h1 className="font-display text-2xl sm:text-3xl font-bold tabular tracking-tight text-slate-900 mt-1">{inv.number}</h1>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <span className={`text-xs px-2 py-0.5 rounded ${STATUS_STYLE[inv.status] || "bg-slate-100"}`}>{inv.status}</span>
                  <span className="text-xs text-slate-500">Issued {fmtDate(inv.created_at)}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">Amount due</div>
                <div className="font-display text-3xl sm:text-4xl font-bold tabular mt-1" style={{ color: balance > 0 ? primary : "#10B981" }}>
                  {fmtCAD(balance)}
                </div>
                <div className="text-[11px] sm:text-xs text-slate-500 mt-1">of {fmtCAD(inv.total || 0)}{inv.due_date ? ` · due ${fmtDate(inv.due_date)}` : ""}</div>
              </div>
            </div>
          </div>

          <div className="px-5 sm:px-8 py-5 grid grid-cols-1 sm:grid-cols-2 gap-5 border-b border-slate-100">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Billed to</div>
              <div className="mt-1 text-sm font-medium text-slate-900">{customer.name}</div>
              {customer.email && <div className="text-xs text-slate-500">{customer.email}</div>}
              {customer.billing_address && <div className="text-xs text-slate-500 whitespace-pre-wrap">{customer.billing_address}</div>}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">From</div>
              <div className="mt-1 text-sm font-medium text-slate-900">{settings.business_name}</div>
              {settings.email && <div className="text-xs text-slate-500">{settings.email}</div>}
              {settings.phone && <div className="text-xs text-slate-500">{settings.phone}</div>}
              {settings.hst_number && <div className="text-[11px] text-slate-400 mt-0.5">HST: {settings.hst_number}</div>}
            </div>
          </div>

          {/* Line items */}
          <div className="px-5 sm:px-8 py-5">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-3">Line items</div>
            <div className="overflow-x-auto -mx-5 sm:mx-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="px-5 sm:px-0 py-2 text-left font-medium">Description</th>
                    <th className="py-2 text-right font-medium w-16">Qty</th>
                    <th className="py-2 text-right font-medium w-24">Rate</th>
                    <th className="px-5 sm:px-0 py-2 text-right font-medium w-24">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(inv.line_items || []).map((li) => {
                    const lineTotal = (Number(li.quantity || 0) * Number(li.rate || 0))
                      - (li.line_discount_type === "percent" ? (Number(li.quantity || 0) * Number(li.rate || 0)) * (Number(li.line_discount_value || 0) / 100) : Number(li.line_discount_value || 0));
                    return (
                      <tr key={li.id} className="border-b border-slate-100">
                        <td className="px-5 sm:px-0 py-3 align-top">
                          <div className="font-medium text-slate-900">{li.name}</div>
                          {li.description && <div className="text-xs text-slate-500 mt-0.5">{li.description}</div>}
                        </td>
                        <td className="py-3 text-right tabular text-slate-700">{li.quantity}</td>
                        <td className="py-3 text-right tabular text-slate-700">{fmtCAD(li.rate)}</td>
                        <td className="px-5 sm:px-0 py-3 text-right tabular font-medium text-slate-900">{fmtCAD(lineTotal)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mt-5 flex justify-end">
              <div className="w-full sm:w-72 space-y-1.5 text-sm">
                <div className="flex justify-between text-slate-600"><span>Subtotal</span><span className="tabular">{fmtCAD(inv.subtotal)}</span></div>
                {inv.invoice_discount_amount > 0 && (
                  <div className="flex justify-between text-slate-600"><span>Discount</span><span className="tabular">-{fmtCAD(inv.invoice_discount_amount)}</span></div>
                )}
                <div className="flex justify-between text-slate-600"><span>HST ({inv.hst_rate || 13}%)</span><span className="tabular">{fmtCAD(inv.hst)}</span></div>
                <div className="flex justify-between font-bold text-slate-900 border-t border-slate-200 pt-2 mt-2 text-base"><span>Total</span><span className="tabular">{fmtCAD(inv.total)}</span></div>
                {inv.amount_paid > 0 && (
                  <div className="flex justify-between text-emerald-700"><span>Paid</span><span className="tabular">-{fmtCAD(inv.amount_paid)}</span></div>
                )}
                <div className="flex justify-between font-bold border-t border-slate-200 pt-2 mt-2 text-base" style={{ color: balance > 0 ? primary : "#10B981" }}><span>Balance due</span><span className="tabular">{fmtCAD(balance)}</span></div>
              </div>
            </div>
          </div>

          {inv.notes_invoice && (
            <div className="px-5 sm:px-8 py-5 border-t border-slate-100 bg-slate-50/60">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Notes</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap mt-1">{inv.notes_invoice}</p>
            </div>
          )}

          {settings.invoice_terms_long && (
            <div className="px-5 sm:px-8 py-5 border-t border-slate-100">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Terms of Service</div>
              <div className="text-xs text-slate-500 whitespace-pre-wrap leading-relaxed max-h-40 overflow-y-auto bg-slate-50 border border-slate-200 rounded p-3">
                {settings.invoice_terms_long}
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-[11px] text-slate-400 mt-6 print:hidden">
          This invoice was sent securely. Questions? Reply to the email you received.
        </p>
      </main>
    </div>
  );
}
